import { IconKrone32, IconMessage32, IconPinMarker32 } from '@warp-ds/icons/react';
import { Breadcrumbs } from '@warp-ds/react';
import { type PropsWithChildren, useEffect, useState } from 'react';
import { SimpleHead } from 'shared/page/Head';
import { HeroBanner } from 'shared/page/HeroBanner';
import { MainStatic } from 'shared/page/Main';
import { Menu } from 'shared/page/Menu';
import type { SeoData } from 'shared/seo/interfaces';
import type { LevelFactory } from '@/components/factory';
import type { Vertical } from '@/data/verticals';
import { createHeading } from '@/utils/heading';
import { trackDestinationPageView } from '@/utils/tracking';
import { DestinationFact } from './DestinationFact';
import styles from './Page.module.css';
import { StructuredData } from './StructuredData';

interface Props {
    factory: LevelFactory;
    vertical: Vertical;
    data: SeoData;
}

export const DestinationPage = ({ factory, vertical, data, children }: PropsWithChildren<Props>) => {
    useEffect(() => {
        trackDestinationPageView(vertical);
    }, []);

    const {
        pagePath,
        destination: { display: destination, image, price, facts },
    } = factory.pageValues();
    const canonical = `https://www.finn.no${data.basePath}${pagePath}`;
    const displayPrice = price && price > 0 ? `fra ${price} kr` : '';

    const insertDestination = (str: string): string => str.replaceAll('$destination', destination);
    const insertContinent = (str: string): string => str.replaceAll('$continent', destination);
    const insertData = (str: string): string => insertDestination(insertContinent(str));

    const siteTitle = insertDestination(data.lang.metaTitleDestination).replace(/\$price/gm, displayPrice);
    const description = insertDestination(data.lang.metaDescriptionDestination);
    const [imageUrl, setImageUrl] = useState(`${data.cdnPath}${image}.jpg`);

    const breadcrumbList = [
        <a href="/reise/" key="breadcrumb1">
            Reise
        </a>,
        <a href={data.basePath} key="breadcrumb2">
            {data.lang.title}
        </a>,
        ...factory.breadcrumbList(),
    ];

    const { shortText, SearchComponent } = factory.menuValues();

    return (
        <>
            <SimpleHead
                title={siteTitle}
                canonical={canonical}
                description={description}
                imageUrl={imageUrl.replace('480x360c', 'watermark-social')}
            />
            <MainStatic className={styles['seo-container']}>
                <h1 className="sr-only">{createHeading(vertical, shortText)}</h1>
                <Breadcrumbs className={`${styles['seo-wrapper']} hidden md:flex my-8`}>{breadcrumbList}</Breadcrumbs>
                <div className={styles['seo-menu-wrapper']}>
                    <Menu activeVertical={vertical} />
                </div>
                <HeroBanner vertical={vertical}>
                    <SearchComponent />
                </HeroBanner>
                <div className={styles['seo-wrapper']}>
                    <section className="my-32" aria-labelledby="seo-about-heading">
                        <h2 id="seo-about-heading" className="sr-only">
                            {`Om ${destination}`}
                        </h2>
                        {Object.keys(data.lang.destination.moneymakers).includes(destination?.toLowerCase()) ? (
                            <>
                                <div className="md:w-1/3 p-16 mb-16 mx-auto">
                                    <img
                                        src={imageUrl}
                                        className="rounded-full"
                                        alt={destination}
                                        data-testid="resultImage"
                                        onError={() => setImageUrl(`${data.cdnPath}default.jpg`)}
                                    />
                                </div>
                                <div className="columns-1 md:columns-2 px-16">
                                    <p className="font-bold">{insertDestination(data.lang.destination.heading)}</p>
                                    <h3 className="h4 pt-16">{insertDestination(data.lang.destination.destinationSubHeading)}</h3>
                                    {data.lang.destination.moneymakers[destination.toLowerCase()].map((text, idx) => (
                                        // biome-ignore lint/suspicious/noArrayIndexKey: This existed before biome was added
                                        <p dangerouslySetInnerHTML={{ __html: text }} key={`moneymakers${idx}`} />
                                    ))}
                                    <h3 className="h4 pt-16">{insertDestination(data.lang.destination.destinationGeneralSubHeading)}</h3>
                                    {data.lang.destination.texts.map((text, idx) => (
                                        // biome-ignore lint/suspicious/noArrayIndexKey: This existed before biome was added
                                        <p dangerouslySetInnerHTML={{ __html: insertData(text) }} key={`description${idx}`} />
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className={`md:grid ${facts ? 'md:grid-cols-3' : 'md:grid-cols-2'} items-center`}>
                                {!facts && (
                                    <div className="p-16">
                                        <img
                                            src={imageUrl}
                                            className="rounded-8 w-full"
                                            alt={destination}
                                            data-testid="resultImage"
                                            onError={() => setImageUrl(`${data.cdnPath}default.jpg`)}
                                        />
                                    </div>
                                )}
                                <div className={`px-16 md:px-32 py-16 text-justify ${facts ? 'md:col-span-2 md:border-r' : ''}`}>
                                    <p className="font-bold">{insertDestination(data.lang.destination.heading)}</p>
                                    {data.lang.destination.texts.map((text, idx) => (
                                        // biome-ignore lint/suspicious/noArrayIndexKey: This existed before biome was added
                                        <p dangerouslySetInnerHTML={{ __html: insertData(text) }} key={`description${idx}`} />
                                    ))}
                                </div>
                                {facts && (
                                    // biome-ignore lint/a11y/useAriaPropsSupportedByRole: intended?
                                    <div className="px-16 md:px-32" aria-labelledby="seo-destination-facts-title">
                                        <h3 id="seo-destination-facts-title" className="font-bold mb-16">
                                            Fakta om {destination}
                                        </h3>
                                        {facts.country && <DestinationFact icon={<IconPinMarker32 />} title="Land" value={facts.country} />}
                                        {facts.capital && (
                                            <DestinationFact icon={<IconPinMarker32 />} title="Hovedstad" value={facts.capital} />
                                        )}
                                        <DestinationFact icon={<IconMessage32 />} title="Offisielt språk" value={facts.language} />
                                        <DestinationFact icon={<IconKrone32 />} title="Valuta" value={facts.currency} />
                                    </div>
                                )}
                            </div>
                        )}
                    </section>
                    <section className="offscreen">{children}</section>
                </div>
            </MainStatic>
            <StructuredData factory={factory} title={data.lang.title} prefix={data.lang.headingToDestination} path={data.basePath} />
        </>
    );
};
